// @ts-nocheck
import React from 'react';
import { Input, Badge, Card, Empty } from 'antd';
import { EditOutlined, FileTextOutlined } from '@ant-design/icons';
import { colors, spacing } from '../design-system/tokens';
import { Typography } from '../design-system/typography';
import { SectionDivider } from '../contract/SectionDivider';

const { TextArea } = Input;

interface GeneratedClauses {
  shipmentNoteOne?: string;
  shipmentNoteTwo?: string;
  packingNotes?: string;
}

interface SnippetPreviewProps {
  clauses: GeneratedClauses;
  fieldOrder: readonly string[];
  fieldLabels: Record<string, string>;
  isDirty?: boolean;
  onFieldEdit?: (field: string, value: string) => void;
  readOnly?: boolean;
}

export const SnippetPreview: React.FC<SnippetPreviewProps> = ({
  clauses,
  fieldOrder,
  fieldLabels,
  isDirty = false,
  onFieldEdit,
  readOnly = false
}) => {
  const hasContent = Object.values(clauses).some(value => value && value.trim());

  if (!hasContent) {
    return (
      <div style={{ textAlign: 'center', padding: spacing['2xl'] }}>
        <Empty 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No clauses generated yet"
        />
      </div>
    );
  }

  const getFieldIcon = (field: string) => {
    switch (field) {
      case 'shipmentNoteOne':
        return '📦';
      case 'shipmentNoteTwo':
        return '⚠️';
      case 'packingNotes':
        return '📋';
      default:
        return '📄';
    }
  };

  const getFieldColor = (field: string) => {
    switch (field) {
      case 'shipmentNoteOne':
        return colors.status.positive;
      case 'shipmentNoteTwo':
        return colors.status.warning;
      case 'packingNotes':
        return colors.primary[60];
      default:
        return colors.text.secondary;
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Dirty indicator */}
      {isDirty && (
        <div style={{
          position: 'absolute',
          top: -spacing.sm,
          right: -spacing.sm,
          zIndex: 1
        }}>
          <Badge 
            count="Modified" 
            style={{ 
              backgroundColor: colors.status.warning,
              fontSize: '11px',
              borderRadius: '4px'
            }} 
          />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
        {fieldOrder.map(field => {
          const value = clauses[field as keyof GeneratedClauses];
          
          if (!value) return null;

          return (
            <div key={field}>
              {/* Field Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                marginBottom: spacing.md
              }}>
                <span style={{ fontSize: '18px' }}>
                  {getFieldIcon(field)}
                </span>
                <Typography 
                  variant="h3" 
                  style={{ 
                    color: getFieldColor(field),
                    margin: 0 
                  }}
                >
                  {fieldLabels[field]}
                </Typography>
                <div style={{
                  width: '4px',
                  height: '20px',
                  backgroundColor: getFieldColor(field),
                  borderRadius: '2px'
                }} />
              </div>

              {/* Field Content */}
              <Card
                size="small"
                bordered={false}
                style={{
                  backgroundColor: colors.background.tertiary,
                  border: `2px solid ${getFieldColor(field)}20`,
                  borderRadius: '8px',
                }}
                bodyStyle={{
                  padding: spacing.lg
                }}
              >
                {readOnly ? (
                  <div style={{
                    fontFamily: 'Reddit Sans, sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    color: colors.text.primary,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {value}
                  </div>
                ) : (
                  <TextArea
                    value={value}
                    onChange={(e) => onFieldEdit?.(field, e.target.value)}
                    autoSize={{ minRows: 3, maxRows: 10 }}
                    style={{
                      fontFamily: 'Reddit Sans, sans-serif',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      border: 'none',
                      backgroundColor: 'transparent',
                      resize: 'none'
                    }}
                    placeholder={`Enter ${fieldLabels[field].toLowerCase()}...`}
                  />
                )}

                {/* Character count indicator */}
                <div style={{
                  marginTop: spacing.sm,
                  textAlign: 'right',
                  fontSize: '11px',
                  color: colors.text.tertiary
                }}>
                  {value.length} characters
                  {value.length > getFieldLengthWarning(field) && (
                    <span style={{ color: colors.status.warning, marginLeft: spacing.xs }}>
                      (approaching limit)
                    </span>
                  )}
                </div>
              </Card>

              {/* Add divider between fields */}
              {field !== fieldOrder[fieldOrder.length - 1] && (
                <SectionDivider style={{ margin: `${spacing.lg} 0` }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Preview Actions */}
      {!readOnly && (
        <div style={{
          marginTop: spacing.xl,
          padding: spacing.md,
          backgroundColor: colors.background.secondary,
          borderRadius: '8px',
          border: `1px solid ${colors.border.default}`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            marginBottom: spacing.sm
          }}>
            <EditOutlined style={{ color: colors.primary[60] }} />
            <Typography variant="label" style={{ color: colors.text.primary }}>
              Manual Editing
            </Typography>
          </div>
          <Typography 
            variant="value" 
            style={{ 
              fontSize: '12px',
              color: colors.text.secondary,
              lineHeight: '1.4'
            }}
          >
            You can edit the generated text directly. Changes will be preserved when you save the shipment.
            Use the snippet builder on the right to add or remove specific clauses.
          </Typography>
        </div>
      )}
    </div>
  );
};

/**
 * Get field length warning threshold
 */
function getFieldLengthWarning(field: string): number {
  switch (field) {
    case 'shipmentNoteOne':
      return 800; // 80% of 1000 char limit
    case 'shipmentNoteTwo':
      return 1600; // 80% of 2000 char limit
    case 'packingNotes':
      return 2400; // 80% of 3000 char limit
    default:
      return 800;
  }
}